import { useEffect, useState } from "react";
import { Banknote, CarTaxiFront, Percent, Users, Radio, Building2 } from "lucide-react";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { DataTable } from "@/components/fleet/DataTable";
import { Stars } from "@/components/Stars";

const TopDrivers = ({ rows }) => (
  <div data-testid="top-drivers-card" className="rounded-2xl border border-blue-100 bg-white p-6">
    <h3 className="text-lg font-semibold text-slate-900">Top rated drivers</h3>
    {rows.length === 0 ? (
      <p className="mt-3 text-sm text-slate-400">No passenger ratings yet.</p>
    ) : (
      <ol className="mt-4 space-y-3">
        {rows.map((d, i) => (
          <li key={d.driver_id} data-testid={`top-driver-${d.driver_id}`} className="flex items-center gap-3">
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-400 text-slate-900" : "bg-blue-50 text-blue-700"}`}>{i + 1}</span>
            <span className="flex-1 font-medium text-slate-900">{d.name}</span>
            <Stars value={d.avg} size="h-3.5 w-3.5" />
            <span className="w-20 text-right font-mono-num text-xs text-slate-500">{d.avg.toFixed(1)} · {d.count}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);
import { api, kina } from "@/lib/api";

export const FleetOverview = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/fleet/summary").then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="h-64 animate-pulse rounded-2xl bg-white" />;
  const pct = s.settings.company_fee_pct;
  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard testId="fleet-stat-gross" label="Total fares (cash)" value={kina(s.totals.gross)} icon={Banknote} hint={`${s.totals.trips} completed trips`} />
        <StatCard testId="fleet-stat-drivers" label="Drivers online" value={`${s.drivers_online} / ${s.drivers_total}`} icon={Users} hint={`${s.pending_drivers} awaiting approval`} />
        <StatCard testId="fleet-stat-vehicles" label="Fleet vehicles" value={s.vehicles} icon={CarTaxiFront} hint={`${s.active_rides} in progress · ${s.scheduled} scheduled`} />
        <StatCard testId="fleet-stat-fee-owing" label={`Platform fee owing (${pct}%)`} value={kina(s.company_fee_outstanding)} icon={Building2} tone="dark" hint={`All-time: ${kina(s.totals.company_fee)}`} />
      </div>
      <TopDrivers rows={s.top_drivers} />
      <div>
        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-900"><Percent className="h-4 w-4 text-blue-700" /> Daily fleet statement</h3>
        <DataTable
          testId="fleet-daily-table"
          rows={s.days}
          empty="No completed trips yet."
          cols={[
            ["Date", (r) => r.date],
            ["Trips", (r) => r.trips],
            ["Fares", (r) => kina(r.gross)],
            ["Driver fees (5%)", (r) => kina(r.driver_fee)],
            [`Comfort Taxi fee (${pct}%)`, (r) => <b>{kina(r.company_fee)}</b>],
            ["Fee status", (r) => <StatusBadge status={r.settled ? "settled" : "unsettled"} />],
          ]}
        />
      </div>
      <p className="flex items-center gap-2 text-xs text-slate-400"><Radio className="h-3.5 w-3.5" /> Fees are calculated on completed cash trips, by Port Moresby date.</p>
    </div>
  );
};
